import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Search,
  FileText,
  ExternalLink,
  X,
  Calendar,
  Copy,
  Clock,
  ShieldOff,
} from "lucide-react";
import { toast } from "sonner";
import { SiteShell } from "@/components/layout/SiteShell";
import { api, formatApiError } from "@/lib/api";

/**
 * /admin/acknowledgments — audit log of every manuscript confidentiality
 * record (pending / accepted / declined). Records persist even after the
 * reader is removed from the project or the project is completed, so this
 * page is the source of truth for any dispute about who was asked, who
 * agreed, who declined, and when.
 *
 * Phase B.2: three-state rendering, production/all scope toggle,
 * data_class badges, state-aware "When" column, state-aware agreement
 * modal copy.
 */
export default function AdminAcknowledgments() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, accepted: 0, pending: 0, declined: 0 });
  const [currentVersion, setCurrentVersion] = useState("");
  const [currentText, setCurrentText] = useState("");
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("production"); // "production" | "all"
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/jobs/admin/manuscript-acknowledgments?scope=${scope}&limit=100`
        );
        if (!alive) return;
        setRows(data.results || []);
        setCounts(
          data.counts || { total: 0, accepted: 0, pending: 0, declined: 0 }
        );
        setCurrentVersion(data.current_version || "");
        setCurrentText(data.current_text || "");
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [scope]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      const reader = r.reader || {};
      const project = r.project || {};
      return [
        reader.email,
        reader.display_name,
        project.title,
        project.author_name,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle));
    });
  }, [rows, q]);

  return (
    <SiteShell>
      <section className="container-page py-12" data-testid="admin-ack-page">
        <div className="flex items-center gap-2 text-[hsl(var(--sb-forest-2))]">
          <ShieldCheck size={18} />
          <span className="text-xs uppercase tracking-[0.16em] font-medium">Admin · Audit log</span>
        </div>
        <h1 className="mt-3 font-serif text-4xl tracking-tight">Manuscript confidentiality records</h1>
        <p className="mt-2 text-muted-foreground max-w-2xl">
          Every time an accepted reader is prompted for the confidentiality
          agreement, a record is created in <em>pending</em> state. The record
          moves to <em>accepted</em> once the reader signs it, or
          <em> declined</em> if they refuse. Records persist even after readers
          are removed from a project or projects close, so this list is the
          source of truth for any dispute.
        </p>

        {/* Stat tiles — four-way (Total / Accepted / Pending / Declined). */}
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat
            label="Total records"
            value={counts.total}
            testid="admin-ack-stat-total"
            tone="default"
          />
          <Stat
            label="Accepted"
            value={counts.accepted}
            testid="admin-ack-stat-accepted"
            tone="accepted"
          />
          <Stat
            label="Pending"
            value={counts.pending}
            testid="admin-ack-stat-pending"
            tone="pending"
          />
          <Stat
            label="Declined"
            value={counts.declined}
            testid="admin-ack-stat-declined"
            tone="declined"
          />
        </div>

        <div className="mt-4 text-[11px] text-muted-foreground" data-testid="admin-ack-current-version">
          Current agreement version: <strong>v{currentVersion || "—"}</strong>
        </div>

        {/* Filter + scope toggle + view current text */}
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              className="sb-input pl-9"
              placeholder="Search reader, email, project or author…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="admin-ack-search"
            />
          </div>
          <div
            className="inline-flex items-center gap-0 rounded-full border border-border bg-background p-0.5 text-[11px]"
            role="radiogroup"
            aria-label="Record scope"
            data-testid="admin-ack-scope-toggle"
          >
            <button
              type="button"
              onClick={() => setScope("production")}
              className={`px-3 py-1 rounded-full font-medium transition-colors ${
                scope === "production"
                  ? "bg-[hsl(var(--sb-forest))]/15 text-[hsl(var(--sb-forest-2))]"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              aria-pressed={scope === "production"}
              data-testid="admin-ack-scope-production"
            >
              Production only
            </button>
            <button
              type="button"
              onClick={() => setScope("all")}
              className={`px-3 py-1 rounded-full font-medium transition-colors ${
                scope === "all"
                  ? "bg-[hsl(var(--sb-forest))]/15 text-[hsl(var(--sb-forest-2))]"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              aria-pressed={scope === "all"}
              data-testid="admin-ack-scope-all"
            >
              Include demo / test
            </button>
          </div>
          <button
            type="button"
            onClick={() =>
              setViewing({
                state: "current",
                version: currentVersion,
                text: currentText,
                heading: `Canonical agreement (v${currentVersion || "?"})`,
              })
            }
            className="btn-ghost border whitespace-nowrap"
            data-testid="admin-ack-view-current"
          >
            <FileText size={14} /> View current text
          </button>
        </div>

        {/* Table */}
        <div className="mt-6 card-soft p-0 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="admin-ack-loading">
              Loading confidentiality records…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center" data-testid="admin-ack-empty">
              <ShieldCheck size={22} className="mx-auto text-muted-foreground" />
              <h3 className="mt-3 font-serif text-2xl">No records yet</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Once accepted readers are prompted for confidentiality, their
                pending/accepted/declined records will appear here.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Reader</th>
                    <th className="px-4 py-3 font-medium">Project</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Version</th>
                    <th className="px-4 py-3 font-medium">When</th>
                    {scope === "all" && (
                      <th className="px-4 py-3 font-medium">Data class</th>
                    )}
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filtered.map((r) => (
                    <AckRow
                      key={r.id}
                      row={r}
                      scope={scope}
                      currentVersion={currentVersion}
                      onView={setViewing}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {viewing && (
        <TextModal
          state={viewing.state}
          heading={viewing.heading}
          version={viewing.version}
          whenAt={viewing.whenAt}
          text={viewing.text}
          onClose={() => setViewing(null)}
        />
      )}
    </SiteShell>
  );
}

/** Mapping of state → chip rendering + "when" label + modal heading tone. */
const STATE_META = {
  accepted: {
    chipClass:
      "border-[hsl(var(--sb-forest))]/30 bg-[hsl(var(--sb-moss))]/40 text-[hsl(var(--sb-forest-2))]",
    icon: ShieldCheck,
    label: "Accepted",
  },
  pending: {
    chipClass:
      "border-[hsl(var(--sb-clay))]/40 bg-[hsl(var(--sb-clay))]/10 text-[hsl(var(--sb-clay-2))]",
    icon: Clock,
    label: "Pending",
  },
  declined: {
    chipClass:
      "border-destructive/40 bg-destructive/10 text-destructive",
    icon: ShieldOff,
    label: "Declined",
  },
  update_required: {
    chipClass:
      "border-[hsl(var(--sb-gold))]/50 bg-[hsl(var(--sb-gold))]/15 text-[hsl(var(--sb-gold-2))]",
    icon: Clock,
    label: "Accepted — update required",
  },
};

const DATA_CLASS_META = {
  production: { label: "Production", tone: "border-border bg-muted/30 text-foreground/80" },
  demo:       { label: "Demo",       tone: "border-[hsl(var(--sb-clay))]/40 bg-[hsl(var(--sb-clay))]/10 text-[hsl(var(--sb-clay))]" },
  private_test:{ label: "Private test", tone: "border-[hsl(var(--sb-gold))]/40 bg-[hsl(var(--sb-gold))]/10 text-[hsl(var(--sb-gold-2))]" },
  admin_test: { label: "Admin test", tone: "border-[hsl(var(--sb-gold))]/40 bg-[hsl(var(--sb-gold))]/10 text-[hsl(var(--sb-gold-2))]" },
  legacy:     { label: "Legacy",     tone: "border-border bg-muted/60 text-muted-foreground" },
};

function AckRow({ row, scope, onView, currentVersion }) {
  const reader = row.reader || {};
  const project = row.project || {};
  const readerRemoved = !reader.email && reader.display_name === "(reader removed)";
  const projectDeleted = !project.title || project.title === "(project deleted)";
  const state = row.state || "pending";
  const meta = row.update_required
    ? { ...STATE_META.update_required, label: `Accepted v${row.version} — update required` }
    : STATE_META[state] || STATE_META.pending;
  const dataClassMeta = DATA_CLASS_META[row.data_class] || DATA_CLASS_META.production;
  const Icon = meta.icon;

  // When column — state-driven.
  let whenAt = null;
  let whenLabel = "—";
  if (state === "accepted" && row.accepted_at) {
    whenAt = new Date(row.accepted_at);
    whenLabel = `Accepted ${whenAt.toLocaleString()}`;
  } else if (state === "pending" && row.pending_since) {
    whenAt = new Date(row.pending_since);
    whenLabel = `Pending since ${whenAt.toLocaleString()}`;
  } else if (state === "declined" && row.declined_at) {
    whenAt = new Date(row.declined_at);
    whenLabel = `Declined ${whenAt.toLocaleString()}`;
  }

  const openModal = () => {
    const whenIso =
      state === "accepted" ? row.accepted_at
      : state === "pending" ? row.pending_since
      : state === "declined" ? row.declined_at
      : null;
    const headingName = reader.display_name || "Reader";
    const headingProject = project.title || "Project";
    const headingVer = state === "pending"
      ? `${row.required_version} required (not accepted)`
      : row.version || row.required_version || "?";
    onView({
      state,
      version: row.version || row.required_version,
      // For pending/declined, the authoritative text is `required_text`
      // (what the reader was asked to sign). For accepted, `text` is
      // what they actually signed; fall back to required_text in case
      // the row predates that field.
      text:
        state === "accepted"
          ? row.text || row.required_text
          : row.required_text || row.text,
      whenAt: whenIso,
      heading: `${headingName} · ${headingProject} · v${headingVer}`,
    });
  };

  return (
    <tr className="hover:bg-muted/20" data-testid={`admin-ack-row-${row.id}`}>
      <td className="px-4 py-3 min-w-[180px]">
        <div className="flex items-center gap-2 min-w-0">
          {reader.avatar_url ? (
            <img
              src={reader.avatar_url}
              alt=""
              className="h-8 w-8 rounded-full object-cover shrink-0"
            />
          ) : (
            <div className="h-8 w-8 rounded-full bg-muted grid place-items-center text-xs font-medium shrink-0">
              {(reader.display_name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            {readerRemoved ? (
              <div className="text-sm italic text-muted-foreground">Reader removed</div>
            ) : (
              <>
                <Link
                  to={`/profile/${reader.id}`}
                  className="font-medium truncate link-underline"
                  data-testid={`admin-ack-reader-${row.id}`}
                >
                  {reader.display_name}
                </Link>
                <div className="text-xs text-muted-foreground truncate">{reader.email}</div>
              </>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 min-w-[200px]">
        {projectDeleted ? (
          <div className="text-sm italic text-muted-foreground">Project deleted</div>
        ) : (
          <div>
            <Link
              to={`/jobs/${project.id}`}
              className="font-medium link-underline"
              data-testid={`admin-ack-project-${row.id}`}
            >
              {project.title}
            </Link>
            <div className="text-xs text-muted-foreground">
              by {project.author_name}
              {project.status ? ` · ${project.status}` : ""}
              {project.listing_type === "demo" ? " · Demo" : ""}
            </div>
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium ${meta.chipClass}`}
          data-testid={`admin-ack-status-${row.id}`}
          data-ack-state={state}
        >
          <Icon size={11} /> {meta.label}
        </span>
      </td>
      <td className="px-4 py-3 text-xs" data-testid={`admin-ack-version-${row.id}`}>
        {state === "pending" ? (
          <span className="font-medium text-[hsl(var(--sb-clay-2))]" data-testid={`admin-ack-not-accepted-${row.id}`}>
            Not accepted — version {row.required_version} required
          </span>
        ) : (
          <>v{row.version || row.required_version || "—"}</>
        )}
        {row.update_required && (
          <div className="mt-1 inline-flex rounded-full border border-[hsl(var(--sb-gold))]/50 bg-[hsl(var(--sb-gold))]/15 px-2 py-0.5 text-[10px] font-semibold text-[hsl(var(--sb-gold-2))]" data-testid={`admin-ack-update-required-${row.id}`}>
            Update to v{currentVersion} pending
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap" data-testid={`admin-ack-when-${row.id}`}>
        <div className="flex items-center gap-1.5">
          <Calendar size={11} />
          {whenLabel}
        </div>
      </td>
      {scope === "all" && (
        <td className="px-4 py-3">
          <span
            className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] ${dataClassMeta.tone}`}
            data-testid={`admin-ack-dataclass-${row.id}`}
          >
            {dataClassMeta.label}
          </span>
        </td>
      )}
      <td className="px-4 py-3 text-right">
        <button
          type="button"
          onClick={openModal}
          className="btn-ghost border text-xs"
          data-testid={`admin-ack-view-${row.id}`}
        >
          <FileText size={12} /> View agreement
        </button>
        {(row.previous_acceptances || []).map((h, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onView({
              state: "accepted",
              version: h.version,
              text: h.text,
              whenAt: h.accepted_at,
              heading: `${reader.display_name || "Reader"} · ${project.title || "Project"} · v${h.version} (earlier acceptance)`,
            })}
            className="mt-1.5 btn-ghost border text-xs block ml-auto"
            data-testid={`admin-ack-view-previous-${row.id}-${i}`}
          >
            <FileText size={12} /> v{h.version} accepted {h.accepted_at ? new Date(h.accepted_at).toLocaleDateString() : ""}
          </button>
        ))}
      </td>
    </tr>
  );
}

function TextModal({ state, heading, version, whenAt, text, onClose }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success("Agreement text copied.");
    } catch {
      toast.error("Couldn't copy — please select the text manually.");
    }
  };
  // State-aware preamble so admins never confuse a pending agreement
  // with one the reader already signed.
  let preamble = null;
  if (state === "accepted" && whenAt) {
    preamble = (
      <p className="mt-1 text-xs text-muted-foreground" data-testid="admin-ack-modal-preamble">
        This is the exact text the reader accepted on {new Date(whenAt).toLocaleString()}.
      </p>
    );
  } else if (state === "pending") {
    preamble = (
      <p
        className="mt-1 text-xs text-[hsl(var(--sb-clay-2))]"
        data-testid="admin-ack-modal-preamble"
      >
        Not accepted — version {version} required. This is the current agreement the
        reader must accept; they have <strong>not</strong> signed any version on this project.
        {whenAt && (
          <> Pending since {new Date(whenAt).toLocaleString()}.</>
        )}
      </p>
    );
  } else if (state === "declined") {
    preamble = (
      <p className="mt-1 text-xs text-destructive" data-testid="admin-ack-modal-preamble">
        This is the agreement the reader was asked to accept — they declined
        {whenAt && <> on {new Date(whenAt).toLocaleString()}</>}.
      </p>
    );
  } else if (state === "current") {
    preamble = (
      <p className="mt-1 text-xs text-muted-foreground">
        Current canonical agreement text. Readers accepted into a project are
        asked to sign this version.
      </p>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="admin-ack-text-modal"
      data-ack-modal-state={state || "current"}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-2xl border bg-background p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[hsl(var(--sb-forest-2))]">
              <ShieldCheck size={18} />
              <span className="text-[11px] uppercase tracking-[0.14em] font-medium">
                Confidentiality agreement · v{version || "?"}
              </span>
            </div>
            <h2 className="mt-1 font-serif text-2xl">{heading}</h2>
            {preamble}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close"
            data-testid="admin-ack-text-modal-close"
          >
            <X size={18} />
          </button>
        </div>
        <div
          className="mt-5 rounded-xl border bg-muted/30 p-4 whitespace-pre-wrap text-sm leading-relaxed"
          data-testid="admin-ack-text-body"
        >
          {text || "(no text captured on this record)"}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={copy}
            className="btn-ghost border"
            data-testid="admin-ack-text-copy"
          >
            <Copy size={14} /> Copy text
          </button>
          <button type="button" onClick={onClose} className="btn-primary">
            <ExternalLink size={14} /> Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, testid, tone }) {
  const toneClass =
    tone === "accepted"
      ? "border-[hsl(var(--sb-forest))]/30 bg-[hsl(var(--sb-moss))]/15"
      : tone === "pending"
      ? "border-[hsl(var(--sb-clay))]/40 bg-[hsl(var(--sb-clay))]/8"
      : tone === "declined"
      ? "border-destructive/40 bg-destructive/5"
      : "";
  return (
    <div className={`card-soft py-4 ${toneClass}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium">
        {label}
      </div>
      <div className="mt-1 font-serif text-2xl">{value}</div>
    </div>
  );
}
